import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { AutoEnrollmentContract } from '@/AutoEnrollment/application'
import {
  AutoEnrollmentModel,
  AutoEnrollmentRepository,
  AutoEnrollmentSteps,
  AutoEnrollmentTypes,
} from '@/AutoEnrollment/domain'
import { ApiStep1And2FormErrorFieldAdapter } from '@/AutoEnrollment/infrastructure/ApiAutoEnrollmentAdapters'
import {
  FillWithOwnerCases,
  FormReturnType,
  IUseCheckCifAlreadyExists,
  Step1Form,
  Step2Form,
  Step3Form,
  useAutoEnrollmentFormManager,
  useCheckCifAlreadyExists,
} from '@/AutoEnrollment/ui/hooks'
import {
  IUseAutoEnrollmentProducts,
  useAutoEnrollmentProducts,
} from '@/AutoEnrollment/ui/hooks/useAutoEnrollmentProducts'
import { LogaltyResponseStatus } from '@/Logalty/domain'
import { IUseLogaltyFirmController, useLogaltyFirmController } from '@/Logalty/ui/hooks'
import { HttpModel, MessagesModel } from '@/Shared/domain'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { navigationRoutes } from '@/Shared/ui/routers'
import { useUserData } from '@/User/ui/context'

import { AutoEnrollmentTranslation } from '../autoEnrollmentTranslation'

export interface IUseAutoEnrollmentController {
  autoEnrollmentStep: AutoEnrollmentSteps
  nextStep: () => void
  previousStep: () => void
  autoEnrollmentModel: AutoEnrollmentModel | null
  getCurrentStepForm: () => FormReturnType
  documents: IUseAutoEnrollmentProducts
  logalty: IUseLogaltyFirmController
  cifController: IUseCheckCifAlreadyExists
  fillWithOwnerData: (fillCase: FillWithOwnerCases) => void
  saveStep1: (data: Step1Form) => Promise<void>
  saveStep2: (data: Step2Form) => Promise<void>
  saveStep3: (data: Step3Form) => Promise<void>
}

export const useAutoEnrollmentController = (
  repository: AutoEnrollmentRepository,
): IUseAutoEnrollmentController => {
  const {
    user: { user },
  } = useUserData()
  const { getCurrentForm, initForms, fillWithOwnerData } = useAutoEnrollmentFormManager()
  const documents = useAutoEnrollmentProducts(repository)
  const cifController = useCheckCifAlreadyExists(repository)
  const { t } = useTranslation()

  const getSignStatusFunction = (): Promise<HttpModel<LogaltyResponseStatus>> => {
    return AutoEnrollmentContract(repository).getAutoEnrollmentLogaltyStatus(
      autoEnrollmentModel?.processType || 1,
    )
  }
  const logalty = useLogaltyFirmController(getSignStatusFunction)

  const navigation = useNavigate()
  const location = useLocation()
  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()

  const numberOfSteps = Object.keys(AutoEnrollmentSteps).length / 2
  const [autoEnrollmentStep, setAutoEnrollmentStep] = useState<AutoEnrollmentSteps>(
    AutoEnrollmentSteps.BILLING_DATA,
  )

  const [autoEnrollmentModel, setAutoEnrollmentModel] =
    useState<AutoEnrollmentModel | null>(null)

  const nextStep = (): void => {
    setAutoEnrollmentStep(Math.min(autoEnrollmentStep + 1, numberOfSteps))
    window.scrollTo(0, 0)
  }

  const previousStep = (): void => {
    setAutoEnrollmentStep(Math.max(autoEnrollmentStep - 1, 1))
    window.scrollTo(0, 0)
  }

  const saveStep1 = async (data: Step1Form): Promise<void> => {
    startLoading()

    const response =
      await AutoEnrollmentContract(repository).saveAutoEnrollmentStep1(data)

    stopLoading()

    if (
      handleMetaResponse(response?.meta, getCurrentForm(1) as UseFormReturn<Step1Form>, {
        notifySuccess: false,
      })
    ) {
      nextStep()
      getAutoEnrollmentData()
    } else {
      // Error management
      const form = getCurrentStepForm() as UseFormReturn<Step1Form>
      if (response?.meta.messages.length > 0) {
        response?.meta?.messages.forEach((errorMessage: MessagesModel) => {
          const errorField = ApiStep1And2FormErrorFieldAdapter(errorMessage)
          if (errorField) {
            if (errorField === 'cif') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              form.setError(errorField as any, {
                type: 'value',
                message: t(
                  AutoEnrollmentTranslation.billingGeneralData.data.cif
                    .alreadyExistsError,
                ),
              })
            } else {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              form.setError(errorField as any, {
                type: 'value',
                message: errorMessage.description,
              })
            }
          }
        })
      }
    }
  }

  const saveStep2 = async (data: Step2Form): Promise<void> => {
    startLoading()

    const response =
      await AutoEnrollmentContract(repository).saveAutoEnrollmentStep2(data)

    stopLoading()

    if (
      handleMetaResponse(response?.meta, getCurrentForm(2) as UseFormReturn<Step1Form>, {
        notifySuccess: false,
      })
    ) {
      await documents.getDefaultConditionsData()
      await documents.getDocumentsData(
        autoEnrollmentModel?.processType || AutoEnrollmentTypes.autoEnrollment,
      )
      nextStep()
    } else {
      // Error management
      const form = getCurrentStepForm() as UseFormReturn<Step1Form>
      if (response?.meta.messages.length > 0) {
        response?.meta?.messages.forEach((errorMessage: MessagesModel) => {
          const errorField = ApiStep1And2FormErrorFieldAdapter(errorMessage)
          if (errorField) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            form.setError(errorField as any, {
              type: 'value',
              message: errorMessage.description,
            })
          }
        })
      }
    }
  }

  const saveStep3 = async (data: Step3Form): Promise<void> => {
    startLoading()

    const obligatoryProductIds = new Set(
      documents.model
        ?.filter(p => !p.productHiringOptional)
        .map(p => Number(p.productCode)) ?? [],
    )

    // Corregir productServices: vacío para obligatorios, del model para opcionales
    data.products?.forEach(product => {
      if (obligatoryProductIds.has(product.productId)) {
        product.productServices = []
      } else {
        const modelProduct = documents.model?.find(
          m => Number(m.productCode) === product.productId,
        )
        product.productServices = [
          ...(modelProduct?.services?.map(s => s.id) ?? []),
          ...(modelProduct?.subProducts?.map(sp => sp.id) ?? []),
        ]
      }
    })

    // Añadir obligatorios que no estén ya en el formulario
    const existingIds = new Set(data.products?.map(p => p.productId) ?? [])
    obligatoryProductIds.forEach(id => {
      if (!existingIds.has(id)) {
        data.products?.push({ productId: id, productServices: [] })
      }
    })

    const response =
      await AutoEnrollmentContract(repository).saveAutoEnrollmentStep3(data)

    stopLoading()

    if (
      handleMetaResponse(response?.meta, getCurrentForm(3) as UseFormReturn<Step3Form>, {
        notifySuccess: false,
      })
    ) {
      if (!response?.data?.signUrl || !response?.data?.signSAML) {
        logalty.setSignStatus(LogaltyResponseStatus.Error)
        setAutoEnrollmentStep(5)
        window.scrollTo(0, 0)
      } else {
        logalty.setLogalty({
          signUrl: response.data.signUrl,
          signSAML: atob(response.data.signSAML),
        })
        nextStep()
      }
    }
  }

  const getAutoEnrollmentData = async (): Promise<void> => {
    startLoading()

    const response = await AutoEnrollmentContract(repository).getAutoEnrollmentData()

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (response.data) {
        setAutoEnrollmentModel(response.data)
        setAutoEnrollmentStep(response.data.step)
        initForms(response.data)

        if (response.data.step >= AutoEnrollmentSteps.DOCUMENTATION) {
          await documents.getDefaultConditionsData()
          await documents.getDocumentsData(response.data.processType)
        }
      }
    }
  }

  const getCurrentStepForm = (): FormReturnType => {
    return getCurrentForm(autoEnrollmentStep)
  }

  useEffect(() => {
    if (
      location.pathname === navigationRoutes.autoEnrollment &&
      user?.autoEnrollmentType !== AutoEnrollmentTypes.autoEnrollment
    ) {
      navigation(navigationRoutes.home)
    }

    getAutoEnrollmentData()
  }, [])

  return {
    autoEnrollmentStep,
    nextStep,
    previousStep,
    autoEnrollmentModel,
    getCurrentStepForm,
    documents,
    logalty,
    cifController,
    fillWithOwnerData,
    saveStep1,
    saveStep2,
    saveStep3,
  }
}
